import React from "react";
import { cn } from "@/lib/utils";

// Building blocks for the user dialog ("Gebruikersvenster" in the design): a
// two-column form row, a titled section, segmented chips and check tiles. A
// field whose staged value differs from the saved one gets the zee outline, so
// the admin can see what the footer's change count refers to.

export const CHANGED_RING = "border-waves-zee ring-1 ring-waves-zee";
export const CHANGED_OUTLINE = "outline outline-2 outline-offset-2 outline-waves-zee";

// `divided` draws the rule above every section but the first one.
export function FormSection({ title, children, divided, className }: { title?: string; children: React.ReactNode; divided?: boolean; className?: string }) {
  return (
    <div className={cn("flex flex-col", divided && "border-t border-waves-line pt-6", className)}>
      {title && <div className="pb-2 text-base font-black">{title}</div>}
      {children}
    </div>
  );
}

export function FieldRow({ label, hint, children, labelExtra }: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
  labelExtra?: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 items-start gap-2 py-3 sm:grid-cols-[200px_minmax(0,1fr)] sm:gap-6">
      <div className="flex flex-col gap-0.5 sm:pt-2.5">
        <div className="text-sm font-bold">{label}</div>
        {hint && <div className="text-[13px] leading-snug text-waves-muted">{hint}</div>}
        {labelExtra}
      </div>
      <div className="flex min-w-0 flex-col gap-2.5">{children}</div>
    </div>
  );
}

export function FieldError({ children }: { children: React.ReactNode }) {
  return <div className="text-[13px] text-status-alert-fg">{children}</div>;
}

export interface SegOption<T> { value: T; label: string }

// Segmented pill control (grey track, ink selection). With `multiple` it
// toggles values in and out of a list instead of picking one.
export function Segmented<T extends string | number>(props: {
  options: SegOption<T>[];
  changed?: boolean;
  "aria-label"?: string;
  className?: string;
} & ({ multiple?: false; value: T; onChange: (v: T) => void } | { multiple: true; value: T[]; onChange: (v: T[]) => void })) {
  const isOn = (v: T) => (props.multiple ? props.value.includes(v) : props.value === v);
  const pick = (v: T) => {
    if (props.multiple) props.onChange(props.value.includes(v) ? props.value.filter(x => x !== v) : [...props.value, v]);
    else props.onChange(v);
  };
  return (
    <div
      role="group"
      aria-label={props["aria-label"]}
      className={cn(
        "inline-flex max-w-full flex-wrap self-start rounded-[18px] bg-status-neutral-bg p-[3px]",
        props.changed && CHANGED_OUTLINE,
        props.className,
      )}
    >
      {props.options.map(o => {
        const on = isOn(o.value);
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={on}
            onClick={() => pick(o.value)}
            className={cn(
              "whitespace-nowrap rounded-pill px-3.5 py-[7px] text-[13px] font-bold transition-colors",
              on ? "bg-waves-ink text-white" : "text-waves-body hover:text-waves-ink",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// A bordered checkbox tile, laid out two per row (vestigingen, partners).
export function CheckTile({ label, checked, changed, onClick, disabled }: {
  label: string; checked: boolean; changed?: boolean; onClick: () => void; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-3 rounded-nav border border-waves-line bg-white px-3.5 py-2.5 text-left text-[15px] text-waves-ink transition-colors hover:bg-waves-subtle disabled:cursor-not-allowed disabled:opacity-60",
        changed && CHANGED_RING,
      )}
    >
      <span className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border-2 text-xs font-black text-white",
        checked ? "border-waves-ink bg-waves-ink" : "border-[#A9B6C2] bg-white",
      )}>
        {checked ? "✓" : ""}
      </span>
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );
}

export function CheckTileGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{children}</div>;
}

// Shown in the "summary" boxes above a group of settings.
export function SummaryBox({ children }: { children: React.ReactNode }) {
  return <div className="rounded-nav bg-waves-mist px-4 py-3 text-sm leading-normal text-waves-ink text-pretty">{children}</div>;
}

export function SubLabel({ children }: { children: React.ReactNode }) {
  return <div className="w-[100px] shrink-0 text-[13px] font-bold text-waves-body">{children}</div>;
}

export function SubRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
      <SubLabel>{label}</SubLabel>
      {children}
    </div>
  );
}

export const sameSet = (a: (string | number)[], b: (string | number)[]) =>
  a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");
